import React, { useState, useEffect } from 'react';
import {
  FileText,
  Plus,
  Search,
  Filter,
  Edit3,
  Trash2,
  Eye,
  BookOpen,
  User,
  Calendar,
  Users,
  Download,
  RefreshCw,
  MoreHorizontal,
  Star,
  Clock,
  List,
  ChevronRight,
  X
} from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import apiService from '../services/api';
import toast from 'react-hot-toast';
import DeleteConfirmationModal from '../components/DeleteConfirmationModal';

// Get base URL from environment or use default
const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || 'https://vs-back.thundergits.com';

// Helper function to get full URL for book files
const getFileUrl = (filePath) => {
  if (!filePath) return '';
  // If it's already a full URL (starts with http), return as is
  if (filePath.startsWith('http')) return filePath;
  // Otherwise, construct the full URL
  // Remove /api from base URL if present and add the file path
  const baseUrl = API_BASE_URL.replace('in/api', 'in'); 
  console.log('Constructed file URL:', `${baseUrl}`,API_BASE_URL);
  return `${baseUrl}/${filePath}`;
};

function ELibrary() {
  const navigate = useNavigate();
  const [books, setBooks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('all');
  const [statusFilter, setStatusFilter] = useState('all');
  const [selectedBook, setSelectedBook] = useState(null);
  const [showDetails, setShowDetails] = useState(false);
  const [selectedChapter, setSelectedChapter] = useState(null);
  const [showChapterReader, setShowChapterReader] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [bookToDelete, setBookToDelete] = useState(null);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [stats, setStats] = useState({
    total: 0,
    published: 0,
    draft: 0,
    totalReaders: 0
  });

  useEffect(() => {
    fetchBooks();
  }, []);

  const fetchBooks = async () => {
    try {
      setLoading(true);
      setError('');

      const response = await apiService.getAllELibraryBooks();

      if (response.data) {
        setBooks(response.data);
        setStats({
          total: response.data.length,
          published: response.data.filter(b => b.status === 'published').length,
          draft: response.data.filter(b => b.status === 'draft').length,
          totalReaders: response.data.reduce((sum, book) => sum + (book.readerCount || 0), 0)
        });
      } else {
        setBooks([]);
        setStats({
          total: 0,
          published: 0,
          draft: 0,
          totalReaders: 0
        });
      }
    } catch (err) {
      setError('Failed to fetch e-library books');
      console.error('Error fetching books:', err);
      toast.error('Failed to load e-library books');
      setBooks([]);
      setStats({
        total: 0,
        published: 0,
        draft: 0,
        totalReaders: 0
      });
    } finally {
      setLoading(false);
    }
  };

  const categories = ['Sacred Texts', 'Mathematics', 'Ayurveda', 'Epic Literature', 'Spirituality', 'Yoga'];

  const handleEditBook = (book) => {
    // Navigate to AddELibrary page with book ID for editing
    navigate(`/elib/add?edit=${book.id}`, { state: { bookData: book } });
  };

  const handleDeleteClick = (book) => {
    setBookToDelete(book);
    setShowDeleteModal(true);
  };

  const handleDeleteConfirm = async () => {
    if (!bookToDelete) return;

    setDeleteLoading(true);

    try {
      const response = await apiService.deleteELibraryBook(bookToDelete.id);

      if (response.success) {
        // Remove book from local state
        setBooks(prevBooks => prevBooks.filter(book => book.id !== bookToDelete.id));

        // Update stats
        setStats(prevStats => ({
          ...prevStats,
          total: prevStats.total - 1,
          published: bookToDelete.status === 'published' ? prevStats.published - 1 : prevStats.published,
          draft: bookToDelete.status === 'draft' ? prevStats.draft - 1 : prevStats.draft,
          totalReaders: prevStats.totalReaders - (bookToDelete.readerCount || 0)
        }));

        toast.success(`"${bookToDelete.name || bookToDelete.title}" has been deleted successfully`);
        setShowDeleteModal(false);
        setBookToDelete(null);
      } else {
        throw new Error(response.message || 'Failed to delete book');
      }
    } catch (error) {
      console.error('Error deleting book:', error);
      toast.error(error.message || 'Failed to delete book. Please try again.');
    } finally {
      setDeleteLoading(false);
    }
  };

  const handleDeleteCancel = () => {
    if (!deleteLoading) {
      setShowDeleteModal(false);
      setBookToDelete(null);
    }
  };

  const filteredBooks = books.filter(book => {
    const bookTitle = book.title || book.name || '';
    const matchesSearch = bookTitle.toLowerCase().includes(searchTerm.toLowerCase()) ||
      book.author?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      book.description?.toLowerCase().includes(searchTerm.toLowerCase());

    const matchesCategory = categoryFilter === 'all' || book.category === categoryFilter;
    const matchesStatus = statusFilter === 'all' || book.status === statusFilter;

    return matchesSearch && matchesCategory && matchesStatus;
  });

  const BookDetailsModal = ({ book, onClose }) => {
    if (!book) return null;

    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
        <div className="bg-white rounded-xl max-w-4xl w-full max-h-[90vh] overflow-y-auto">
          <div className="p-6 border-b border-gray-200">
            <div className="flex items-center justify-between">
              <h2 className="text-xl font-semibold text-gray-900">Book Details</h2>
              <button onClick={onClose} className="text-gray-400 hover:text-gray-600">
                <X className="w-6 h-6" />
              </button>
            </div>
          </div>

          <div className="p-6">
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
              <div className="lg:col-span-1">
                <img
                  src={getFileUrl(book.icon)}
                  alt={book.title}
                  className="w-full h-80 object-cover rounded-lg shadow-md"
                />
              </div>

              <div className="lg:col-span-2 space-y-4">
                <div>
                  <h3 className="text-2xl font-bold text-gray-900 mb-2">{book.title || book.name}</h3>
                  <p className="text-lg text-gray-600 mb-4">by {book.author}</p>
                  <p className="text-gray-700">{book.description}</p>
                  {book.hasChapter && (
                    <div className="mt-4">
                      <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-blue-100 text-blue-800">
                        <List className="w-4 h-4 mr-2" />
                        {book.chapters?.length || 0} Chapters
                      </span>
                    </div>
                  )}
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div className="bg-blue-50 p-4 rounded-lg">
                    <p className="text-sm text-blue-600 font-medium">Readers</p>
                    <p className="text-2xl font-bold text-blue-800">{(book.readerCount || 0).toLocaleString()}</p>
                  </div>
                  <div className="bg-green-50 p-4 rounded-lg">
                    <p className="text-sm text-green-600 font-medium">Pages</p>
                    <p className="text-2xl font-bold text-green-800">{book.pages}</p>
                  </div>
                  <div className="bg-purple-50 p-4 rounded-lg">
                    <p className="text-sm text-purple-600 font-medium">File Size</p>
                    <p className="text-2xl font-bold text-purple-800">{book.fileSize}</p>
                  </div>
                  <div className="bg-orange-50 p-4 rounded-lg">
                    <p className="text-sm text-orange-600 font-medium">Language</p>
                    <p className="text-2xl font-bold text-orange-800">{book.language}</p>
                  </div>
                </div>

                <div className="flex items-center justify-between pt-4">
                  <span className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-medium ${book.status === 'published' ? 'bg-green-100 text-green-800' : 'bg-yellow-100 text-yellow-800'
                    }`}>
                    {book.status === 'published' ? 'Published' : 'Draft'}
                  </span>
                  <div className="text-sm text-gray-500">
                    Created: {new Date(book.createdAt).toLocaleDateString()}
                  </div>
                </div>

                {/* Chapters List - Show only if book has chapters */}
                {book.hasChapter && book.chapters && book.chapters.length > 0 && (
                  <div className="pt-4">
                    <h4 className="text-lg font-semibold text-gray-900 mb-3">Chapters</h4>
                    <div className="space-y-2 max-h-60 overflow-y-auto">
                      {book.chapters
                        .sort((a, b) => (a.order || 0) - (b.order || 0))
                        .map((chapter) => (
                          <div key={chapter.id} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors">
                            <div className="flex-1">
                              <h5 className="font-medium text-gray-900">{chapter.name}</h5>
                              <p className="text-sm text-gray-600">{chapter.description}</p>
                              {chapter.startPage && chapter.endPage && (
                                <p className="text-xs text-gray-500">Pages {chapter.startPage}-{chapter.endPage}</p>
                              )}
                            </div>
                            <div className="flex space-x-2 ml-4">
                              <button
                                onClick={() => {
                                  if (book.pdfUrl && chapter.startPage) {
                                    try {
                                      // Open PDF at specific page using PDF.js viewer
                                      const pdfUrl = `${getFileUrl(book.pdfUrl)}#page=${chapter.startPage}`;
                                      const newWindow = window.open(pdfUrl);
                                      if (!newWindow) {
                                        toast.error('Please allow popups to read the chapter');
                                      } else {
                                        toast.success(`Opening ${chapter.name} at page ${chapter.startPage}...`);
                                      }
                                    } catch (error) {
                                      console.error('Error opening chapter PDF:', error);
                                      toast.error('Failed to open chapter. Please try again.');
                                    }
                                  } else if (chapter.pdfUrl) {
                                    // Fallback to old method if startPage not available
                                    try {
                                      const newWindow = window.open(getFileUrl(chapter.pdfUrl));
                                      if (!newWindow) {
                                        toast.error('Please allow popups to read the chapter');
                                      } else {
                                        toast.success(`Opening ${chapter.name}...`);
                                      }
                                    } catch (error) {
                                      console.error('Error opening chapter PDF:', error);
                                      toast.error('Failed to open chapter. Please try again.');
                                    }
                                  } else {
                                    toast.error('Chapter PDF not available');
                                  }
                                }}
                                className="p-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
                                title="View Details - Open at Starting Page"
                              >
                                <Eye className="w-4 h-4" />
                              </button>
                            </div>
                          </div>
                        ))}
                    </div>
                  </div>
                )}

                {/* Action Buttons - Show different buttons based on hasChapter */}
                <div className="flex space-x-3 pt-6">
                  {!book.hasChapter ? (
                    // For books without chapters - show direct read/download buttons
                    <>
                      <button
                        onClick={() => {
                          if (book.pdfUrl) {
                            try {
                              const newWindow = window.open(getFileUrl(book.pdfUrl));
                              if (!newWindow) {
                                toast.error('Please allow popups to read the book');
                              } else {
                                toast.success('Opening book in new tab...');
                              }
                            } catch (error) {
                              console.error('Error opening PDF:', error);
                              toast.error('Failed to open book. Please try again.');
                            }
                          } else {
                            toast.error('PDF file not available for this book');
                          }
                        }}
                        className="flex-1 bg-blue-600 text-white py-3 px-4 rounded-lg hover:bg-blue-700 transition-colors duration-200 font-medium flex items-center justify-center"
                      >
                        <BookOpen className="w-5 h-5 mr-2" />
                        Read Book
                      </button>
                      <button
                        onClick={() => {
                          if (book.pdfUrl) {
                            try {
                              const link = document.createElement('a');
                              link.href = getFileUrl(book.pdfUrl);
                              link.download = `${book.title || book.name}.pdf`;
                              link.target = '_blank';
                              document.body.appendChild(link);
                              link.click();
                              document.body.removeChild(link);
                              toast.success('Download started');
                            } catch (error) {
                              console.error('Error downloading PDF:', error);
                              toast.error('Failed to download book. Please try again.');
                            }
                          } else {
                            toast.error('PDF file not available for download');
                          }
                        }}
                        className="flex-1 bg-green-600 text-white py-3 px-4 rounded-lg hover:bg-green-700 transition-colors duration-200 font-medium flex items-center justify-center"
                      >
                        <Download className="w-5 h-5 mr-2" />
                        Download Book
                      </button>
                    </>
                  ) : (
                    // For books with chapters - show chapter-based actions
                    <div className="w-full text-center py-2">
                      <p className="text-gray-600 text-sm">
                        This book is organized into chapters. Use the chapter buttons above to read or download individual chapters.
                      </p>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <RefreshCw className="w-8 h-8 animate-spin text-orange-600" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="space-y-6">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between">
          <div>
            <h1 className="text-3xl font-bold text-gray-900 flex items-center">
              E-Library
            </h1>
            <p className="mt-2 text-gray-600">
              Manage digital books, texts, and spiritual literature
            </p>
          </div>
          <div className="mt-4 sm:mt-0 flex space-x-3">
            <button
              onClick={fetchBooks}
              className="inline-flex items-center px-4 py-2 bg-orange-600 text-white font-medium rounded-lg hover:bg-orange-700 transition-colors duration-200"
            >
              <RefreshCw className="w-4 h-4 mr-2" />
              Retry
            </button>
            <button
              onClick={() => navigate('/elib/add')}
              className="inline-flex items-center px-4 py-2 bg-gray-100 text-gray-700 font-medium rounded-lg hover:bg-gray-200 transition-colors duration-200"
            >
              <Plus className="w-4 h-4 mr-2" />
              Add Book
            </button>
          </div>
        </div>

        {/* Error State */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-8">
          <div className="text-center">
            <div className="mx-auto flex items-center justify-center h-12 w-12 rounded-full bg-red-100">
              <FileText className="h-6 w-6 text-red-600" />
            </div>
            <h3 className="mt-2 text-sm font-medium text-gray-900">Failed to load books</h3>
            <p className="mt-1 text-sm text-gray-500">{error}</p>
            <div className="mt-6">
              <button
                onClick={fetchBooks}
                className="inline-flex items-center px-4 py-2 bg-orange-600 text-white font-medium rounded-lg hover:bg-orange-700 transition-colors duration-200"
              >
                <RefreshCw className="w-4 h-4 mr-2" />
                Try Again
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className="text-3xl font-bold text-gray-900 flex items-center">
            E-Library
          </h1>
          <p className="mt-2 text-gray-600">
            Manage digital books, texts, and spiritual literature
          </p>
        </div>
        <div className="mt-4 sm:mt-0 flex space-x-3">
          <button
            onClick={fetchBooks}
            className="inline-flex items-center px-4 py-2 bg-gray-100 text-gray-700 font-medium rounded-lg hover:bg-gray-200 transition-colors duration-200"
          >
            <RefreshCw className="w-4 h-4 mr-2" />
            Refresh
          </button>
          <button className="inline-flex items-center px-4 py-2 bg-gray-100 text-gray-700 font-medium rounded-lg hover:bg-gray-200 transition-colors duration-200">
            <Download className="w-4 h-4 mr-2" />
            Export
          </button>
          <button
            onClick={() => navigate('/elib/add')}
            className="inline-flex items-center px-4 py-2 bg-orange-600 text-white font-medium rounded-lg hover:bg-orange-700 transition-colors duration-200"
          >
            <Plus className="w-4 h-4 mr-2" />
            Add Book
          </button>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
          <div className="flex items-center">
            <div className="p-2 bg-blue-100 rounded-lg">
              <BookOpen className="w-6 h-6 text-blue-600" />
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-600">Total Books</p>
              <p className="text-2xl font-bold text-gray-900">{stats.total}</p>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
          <div className="flex items-center">
            <div className="p-2 bg-green-100 rounded-lg">
              <Eye className="w-6 h-6 text-green-600" />
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-600">Published</p>
              <p className="text-2xl font-bold text-gray-900">{stats.published}</p>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
          <div className="flex items-center">
            <div className="p-2 bg-yellow-100 rounded-lg">
              <Edit3 className="w-6 h-6 text-yellow-600" />
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-600">Drafts</p>
              <p className="text-2xl font-bold text-gray-900">{stats.draft}</p>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
          <div className="flex items-center">
            <div className="p-2 bg-purple-100 rounded-lg">
              <Users className="w-6 h-6 text-purple-600" />
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-600">Total Readers</p>
              <p className="text-2xl font-bold text-gray-900">{stats.totalReaders.toLocaleString()}</p>
            </div>
          </div>
        </div>
      </div>

      {/* Filters */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Search</label>
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
              <input
                type="text"
                placeholder="Search books, authors..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10 w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
              />
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Category</label>
            <select
              value={categoryFilter}
              onChange={(e) => setCategoryFilter(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
            >
              <option value="all">All Categories</option>
              {categories.map((category) => (
                <option key={category} value={category}>
                  {category}
                </option>
              ))}
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Status</label>
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
            >
              <option value="all">All Status</option>
              <option value="published">Published</option>
              <option value="draft">Draft</option>
            </select>
          </div>

          <div className="flex items-end">
            <button
              onClick={() => {
                setSearchTerm('');
                setCategoryFilter('all');
                setStatusFilter('all');
              }}
              className="w-full px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors duration-200"
            >
              Clear Filters
            </button>
          </div>
        </div>
      </div>

      {/* Books Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
        {filteredBooks.length > 0 ? (
          filteredBooks.map((book) => (
            <div key={book.id} className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden hover:shadow-md transition-shadow duration-200">
              <div className="aspect-w-3 aspect-h-4">
                <img
                  src={getFileUrl(book.icon)}
                  alt={book.title}
                  className="w-full h-48 object-cover"
                />
              </div>

              <div className="p-4">
                <div className="flex items-start justify-between mb-2">
                  <h3 className="text-lg font-semibold text-gray-900 line-clamp-2">{book.title || book.name}</h3>
                  <div className="flex flex-col items-end space-y-1">
                    <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${book.status === 'published' ? 'bg-green-100 text-green-800' : 'bg-yellow-100 text-yellow-800'
                      }`}>
                      {book.status === 'published' ? 'Published' : 'Draft'}
                    </span>
                    {book.hasChapter && (
                      <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                        <List className="w-3 h-3 mr-1" />
                        {book.chapters?.length || 0} Ch.
                      </span>
                    )}
                  </div>
                </div>

                <div className="space-y-2 mb-4">
                  <div className="flex items-center text-sm text-gray-600">
                    <User className="w-4 h-4 mr-2" />
                    <span className="truncate">{book.author}</span>
                  </div>

                  <div className="flex items-center text-sm text-gray-600">
                    <Users className="w-4 h-4 mr-2" />
                    <span>{(book.readerCount || 0).toLocaleString()} readers</span>
                  </div>

                  <div className="flex items-center text-sm text-gray-600">
                    <Calendar className="w-4 h-4 mr-2" />
                    <span>{new Date(book.createdAt).toLocaleDateString()}</span>
                  </div>
                </div>

                <p className="text-gray-600 text-sm mb-4 line-clamp-2">{book.description}</p>

                <div className="flex space-x-2">
                  <button
                    onClick={() => {
                      setSelectedBook(book);
                      setShowDetails(true);
                    }}
                    className="flex-1 bg-orange-600 text-white py-2 px-3 rounded-lg hover:bg-orange-700 transition-colors duration-200 text-sm font-medium flex items-center justify-center"
                  >
                    <Eye className="w-4 h-4 mr-2" />
                    {book.hasChapter ? 'View Chapters' : 'View Details'}
                  </button>
                  <button
                    onClick={() => handleEditBook(book)}
                    className="p-2 text-gray-400 hover:text-gray-600 border border-gray-300 rounded-lg hover:bg-gray-50"
                    title="Edit Book"
                  >
                    <Edit3 className="w-4 h-4" />
                  </button>
                  <button
                    onClick={() => handleDeleteClick(book)}
                    className="p-2 text-red-400 hover:text-red-600 border border-gray-300 rounded-lg hover:bg-red-50"
                    title="Delete Book"
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              </div>
            </div>
          ))
        ) : (
          <div className="col-span-full text-center py-12">
            <FileText className="mx-auto h-12 w-12 text-gray-400" />
            <h3 className="mt-2 text-sm font-medium text-gray-900">No books found</h3>
            <p className="mt-1 text-sm text-gray-500">
              No books match your current filters.
            </p>
            <div className="mt-6">
              <button
                onClick={() => navigate('/elib/add')}
                className="inline-flex items-center px-4 py-2 bg-orange-600 text-white font-medium rounded-lg hover:bg-orange-700 transition-colors duration-200"
              >
                <Plus className="w-4 h-4 mr-2" />
                Add First Book
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Book Details Modal */}
      {showDetails && (
        <BookDetailsModal
          book={selectedBook}
          onClose={() => setShowDetails(false)}
        />
      )}

      {/* Delete Confirmation Modal */}
      <DeleteConfirmationModal
        isOpen={showDeleteModal}
        onClose={handleDeleteCancel}
        onConfirm={handleDeleteConfirm}
        title="Delete Book"
        message="Are you sure you want to delete this book? This action will permanently remove the book and all its associated data from the library."
        itemName={bookToDelete ? (bookToDelete.name || bookToDelete.title) : ""}
        confirmText="Delete Book"
        cancelText="Cancel"
        loading={deleteLoading}
        danger={true}
      />
    </div>
  );
}

export default ELibrary;
